// Submit Request Page - Zero friction feedback submission
import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  Alert,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  BugReport,
  HelpOutline,
  Lightbulb,
  Feedback,
  Screenshot,
  AttachFile,
  Close,
  CheckCircle,
} from '@mui/icons-material';
import { usePortal } from './PortalLayout';
import { ticketsService } from '@/services/collections/tickets';
import { featuresService } from '@/services/collections/features';
import { notificationsService } from '@/services/collections/notifications';
import { verifyTurnstileToken } from '@/services/turnstile';
import { TurnstileWidget, type TurnstileWidgetRef } from '@/components/common/TurnstileWidget';
import type { TicketType } from '@/types';

const REQUEST_TYPES = [
  { value: 'bug', label: 'Bug', icon: <BugReport />, color: '#ef4444' },
  { value: 'support', label: 'Help', icon: <HelpOutline />, color: '#3b82f6' },
  { value: 'feature_request', label: 'Feature', icon: <Lightbulb />, color: '#f59e0b' },
  { value: 'feedback', label: 'Feedback', icon: <Feedback />, color: '#10b981' },
];

export const SubmitRequestPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { organization, project, branding } = usePortal();

  const [type, setType] = useState<TicketType>(
    (searchParams.get('type') as TicketType) || 'support'
  );
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [screenshots, setScreenshots] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileWidgetRef>(null);

  // Get saved email from localStorage
  useEffect(() => {
    const savedEmail = localStorage.getItem('user_email');
    const savedName = localStorage.getItem('user_name');
    if (savedEmail) setEmail(savedEmail);
    if (savedName) setName(savedName);
  }, []);

  // Handle screenshot capture (placeholder - actual implementation in widget)
  const handleScreenshot = () => {
    // TODO: Implement screenshot capture
    alert('Screenshot capture is available in the embedded widget');
  };

  // Handle file attachment
  const handleAttachment = () => {
    // TODO: Implement file upload
    alert('File upload coming soon');
  };

  // Submit request
  const handleSubmit = async () => {
    if (!organization || !project) return;
    if (!email) {
      setError('Please enter your email address');
      return;
    }
    if (!subject.trim()) {
      setError('Please enter a subject');
      return;
    }
    if (!description.trim()) {
      setError('Please enter a description');
      return;
    }
    if (!turnstileToken) {
      setError('Please complete the verification challenge');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Verify Turnstile token server-side
      const isValid = await verifyTurnstileToken(turnstileToken);
      if (!isValid) {
        setError('Verification failed. Please try again.');
        turnstileRef.current?.reset();
        setTurnstileToken(null);
        setSubmitting(false);
        return;
      }

      // Save email for future use
      localStorage.setItem('user_email', email);
      if (name) localStorage.setItem('user_name', name);

      if (type === 'feature_request') {
        // Create feature request
        await featuresService.createForProject(
          organization.id,
          project.id,
          {
            title: subject,
            description,
            status: 'under_review',
            submitterEmail: email,
            submitterName: name || undefined,
            voterIds: [],
            voteCount: 1, // Auto-vote for submitter
            isPublic: true,
            linkedTicketIds: [],
            tags: [],
            createdBy: 'anonymous',
          } as any,
          'anonymous'
        );
      } else {
        // Create ticket
        const ticketNumber = Date.now(); // TODO: Auto-increment
        const ticketId = await ticketsService.createForProject(
          organization.id,
          project.id,
          {
            ticketNumber,
            type,
            status: 'new',
            priority: 'medium',
            subject,
            description,
            submitterEmail: email,
            submitterName: name || undefined,
            sessionContext: {
              url: window.location.href,
              referrer: document.referrer,
              browser: navigator.userAgent.split(' ').pop() || 'Unknown',
              os: navigator.platform,
              device: /Mobile|Android|iPhone/.test(navigator.userAgent) ? 'Mobile' : 'Desktop',
              viewport: `${window.innerWidth}x${window.innerHeight}`,
              userAgent: navigator.userAgent,
              timestamp: new Date() as any,
            },
            screenshots,
            attachments: [],
            tags: [],
            createdBy: 'anonymous',
          } as any,
          'anonymous'
        );

        // Queue notification to agents using real ticket data
        if (organization.settings?.supportEmail) {
          try {
            await notificationsService.notifyAgentsOfNewTicket(
              {
                id: ticketId,
                ticketNumber,
                subject,
                description,
                submitterEmail: email,
                submitterName: name || undefined,
                projectId: project.id,
              } as any,
              organization,
              [organization.settings.supportEmail],
              window.location.origin
            );
          } catch (notifErr) {
            console.error('Failed to queue notification:', notifErr);
          }
        }
      }

      setSubmitted(true);
      setTurnstileToken(null);
      turnstileRef.current?.reset();
    } catch (err) {
      console.error('Error submitting request:', err);
      setError('Failed to submit. Please try again.');
      turnstileRef.current?.reset();
      setTurnstileToken(null);
    } finally {
      setSubmitting(false);
    }
  };

  // Success state
  if (submitted) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Thank you!
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 4 }}>
          {type === 'feature_request'
            ? "Your feature request has been submitted. We'll review it soon!"
            : "Your request has been submitted. We'll get back to you shortly."}
        </Typography>
        <Stack direction="row" spacing={2} justifyContent="center">
          <Button variant="outlined" onClick={() => navigate(`/p/${organization?.slug}`)}>
            Back to Help Center
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setSubmitted(false);
              setSubject('');
              setDescription('');
              setScreenshots([]);
              setTurnstileToken(null);
            }}
          >
            Submit Another
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto' }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Submit a Request
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 4 }}>
        Let us know how we can help. We typically respond within 24 hours.
      </Typography>

      <Card>
        <CardContent sx={{ p: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Request Type */}
          <Typography variant="subtitle2" gutterBottom>
            What type of request is this?
          </Typography>
          <ToggleButtonGroup
            value={type}
            exclusive
            onChange={(_, val) => val && setType(val)}
            sx={{ mb: 3, flexWrap: 'wrap' }}
          >
            {REQUEST_TYPES.map((rt) => (
              <ToggleButton
                key={rt.value}
                value={rt.value}
                sx={{
                  px: 2,
                  py: 1,
                  '&.Mui-selected': {
                    bgcolor: `${rt.color}20`,
                    borderColor: rt.color,
                    color: rt.color,
                  },
                }}
              >
                {rt.icon}
                <Typography sx={{ ml: 1 }}>{rt.label}</Typography>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          {/* Email */}
          {project?.widgetSettings.collectEmail && (
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required={project.widgetSettings.requireEmail}
              sx={{ mb: 2 }}
              placeholder="your@email.com"
            />
          )}

          {/* Name */}
          {project?.widgetSettings.collectName && (
            <TextField
              fullWidth
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              sx={{ mb: 2 }}
              placeholder="Your name (optional)"
            />
          )}

          {/* Subject */}
          <TextField
            fullWidth
            label={type === 'feature_request' ? 'Feature Title' : 'Subject'}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            required
            sx={{ mb: 2 }}
            placeholder={
              type === 'bug'
                ? 'Describe the bug briefly'
                : type === 'feature_request'
                ? 'What feature would you like?'
                : 'How can we help?'
            }
          />

          {/* Description */}
          <TextField
            fullWidth
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
            multiline
            rows={5}
            sx={{ mb: 2 }}
            placeholder={
              type === 'bug'
                ? 'Steps to reproduce:\n1. \n2. \n3. \n\nExpected behavior:\n\nActual behavior:'
                : type === 'feature_request'
                ? 'Describe the feature and why it would be useful...'
                : 'Provide as much detail as possible...'
            }
          />

          {/* Screenshots */}
          {screenshots.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Screenshots
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {screenshots.map((url, i) => (
                  <Box key={i} sx={{ position: 'relative' }}>
                    <Box
                      component="img"
                      src={url}
                      sx={{
                        width: 80,
                        height: 60,
                        objectFit: 'cover',
                        borderRadius: 1,
                      }}
                    />
                    <IconButton
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        bgcolor: 'background.paper',
                      }}
                      onClick={() =>
                        setScreenshots((prev) => prev.filter((_, idx) => idx !== i))
                      }
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          {/* Turnstile CAPTCHA */}
          <TurnstileWidget
            ref={turnstileRef}
            onSuccess={(token) => setTurnstileToken(token)}
            onExpire={() => setTurnstileToken(null)}
            onError={() => setTurnstileToken(null)}
          />

          {/* Actions */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 1,
            }}
          >
            <Stack direction="row" spacing={1}>
              {project?.features.screenshotCapture && type === 'bug' && (
                <Button
                  variant="outlined"
                  startIcon={<Screenshot />}
                  onClick={handleScreenshot}
                  size="small"
                >
                  Screenshot
                </Button>
              )}
              <Button
                variant="outlined"
                startIcon={<AttachFile />}
                onClick={handleAttachment}
                size="small"
              >
                Attach File
              </Button>
            </Stack>

            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting}
              sx={{
                minWidth: 120,
                bgcolor: branding.primaryColor,
                '&:hover': { bgcolor: branding.primaryColor },
              }}
            >
              {submitting ? <CircularProgress size={24} /> : 'Submit'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
